<script lang="ts">
    import type {Criterion} from '../../../search/criteria.types';
    import {pipe} from '../../../svelte/utils';
    import {loc} from '../../../svelte/pipes';
    import KpValueEditor from '../../value-editors/kp-value-editor/KpValueEditor.svelte';
    import {createEventDispatcher, onDestroy} from 'svelte';
    import KpValueEditorForceSettings
        from '../../value-editors/kp-value-editor-force-settings/KpValueEditorForceSettings.svelte';
    import type {ForceSetting} from '../../value-editors/kp-value-editor-force-settings/types';
    import SearchFacetsAcceptableEditorOptionLabel from './SearchFacetsAcceptableEditorOptionLabel.svelte';
    import {Subject} from 'rxjs';
    import {debounceTime} from 'rxjs/operators';
    import type {SvelteComponentConstructor} from '../../../svelte/types';
    import {Disclosure, DisclosureButton, DisclosurePanel} from '@rgossiaux/svelte-headlessui';
    import {
        createValueEditorsLabelContext,
        setValueEditorLabelContext
    } from '../../value-editors/label/value-editor-label-context';


    export let criterion: Criterion;
    export let initiallyExpanded = false;

    const isOfRangeValueEditorType = criterion.field.editor.type === 'range';
    const dispatch = createEventDispatcher<{'criterion-change': void}>();

    const debouncedCriterionChangedEvent$: Subject<void> = new Subject();

    // debounce for range editor to prevent multiple searches for every slider movement
    const subscription = debouncedCriterionChangedEvent$.pipe(debounceTime(isOfRangeValueEditorType ? 250 : 0))
        .subscribe(() => dispatch('criterion-change'));

    const facetLabelContext = createValueEditorsLabelContext(criterion.field.id, criterion.field.text);
    setValueEditorLabelContext(facetLabelContext);

    onDestroy(() => subscription.unsubscribe());

    function dispatchCriterionChangedEvent() {
        debouncedCriterionChangedEvent$.next();
    }

    const acceptableEditorSettings: ForceSetting<'single-acceptable'> = {
        type: 'acceptable',
        options: {
            switchToInlineModeThreshold: 0,
            optionLabelComponent: SearchFacetsAcceptableEditorOptionLabel as SvelteComponentConstructor<{option}>
        },
    }

    const singleAcceptableEditorSettings: ForceSetting<'single-acceptable'> = {
        type: 'single-acceptable',
        options: {
            switchToInlineModeThreshold: 0,
            optionLabelComponent: SearchFacetsAcceptableEditorOptionLabel as SvelteComponentConstructor<{option}>
        },
    }

    const multipleAcceptableEditorSettings: ForceSetting<'multiple-acceptable'> = {
        type: 'multiple-acceptable',
        options: {
            switchToInlineModeThreshold: 0,
            showFirstCount: 5,
            optionLabelComponent: SearchFacetsAcceptableEditorOptionLabel as SvelteComponentConstructor<{option}>
        }
    }
</script>


<template>
    <Disclosure class="facet" defaultOpen={initiallyExpanded} let:open={expanded}>
        <DisclosureButton class="facet-name btn btn-default btn-xs btn-block" type="button">
            <span id={facetLabelContext.labelId}>
                {pipe(criterion.field, loc())}
            </span>
            <span class="glyphicon glyphicon-triangle-bottom triangle-glyph" class:expanded aria-hidden="true"></span>
        </DisclosureButton>

        {#if expanded}
            <!-- we need to expand manually, otherwise editors won't get initialized properly (e.g. range slider without color) -->
            <DisclosurePanel class="facet-keys" static>
                <KpValueEditorForceSettings forceSettings={[acceptableEditorSettings, singleAcceptableEditorSettings, multipleAcceptableEditorSettings]}>
                    <KpValueEditor editorId={criterion.field.id}
                                   editorName={criterion.field.id}
                                   {...criterion.field.editor}
                                   bind:model={criterion.value}
                                   on:model-change={dispatchCriterionChangedEvent}/>
                </KpValueEditorForceSettings>
            </DisclosurePanel>
        {/if}
    </Disclosure>
</template>

<style lang="less" global>
    .facet {
        .triangle-glyph {
            &::before {
                display: block;
                transition: transform 0.2s;
            }
            &.expanded::before {
                transform: rotate(-180deg);
            }
        }

        .facet-keys {
            padding: 5px 10px;
        }

        .facet-name {
            border-radius: 0;
            border: none;
            box-shadow: none;
            border-top: 1px solid lightgray;
            text-transform: uppercase;
            display: block;
            width: 100%;
            padding-top: 2px;
            text-align: center;
            margin-bottom: 10px;

            + .facet-keys {
                margin-top: -10px;
            }
        }
    }
</style>